// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { Dictionary } from "@insite/client-framework/Common/Types";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { dataViewNotFound, getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { UpdateSearchFieldsType } from "@insite/client-framework/Types/UpdateSearchFieldsType";
import { IEventCollectionModel, IEventListParams } from "@vanmeter/App/Services/EventListService";
import { Draft } from "immer";

export type EventListDataView = {
    value?: IEventCollectionModel;
    isLoading: boolean;
};

export type EventListState = {
    dataViews: Dictionary<EventListDataView>;
    params: IEventListParams;
};

const initialEventListState: EventListState = {
    dataViews: {},
    params: {
        pageSize: 6,
        page: 1,
    },
};

const xEventList = createTypedReducerWithImmer(initialEventListState, {
    "xEventList/UpdateFields": (
        draft: Draft<EventListState>,
        action: { parameter: IEventListParams & UpdateSearchFieldsType },
    ) => {
        const { type } = action.parameter;
        delete action.parameter.type;
        if (type === "Replace") {
            draft.params = action.parameter;
        } else if (type === "Initialize") {
            draft.params = { ...initialEventListState.params, ...action.parameter };
        } else {
            draft.params = { ...draft.params, ...action.parameter };

            for (const key in draft.params) {
                const value = (<any>draft.params)[key];

                // remove empty parameters
                if (value === "" || value === undefined) {
                    delete (<any>draft.params)[key];
                }
            }

            for (const key in action.parameter) {
                // go back to page 1 if any other parameters changed
                if (draft.params.page && draft.params.page > 1 && key !== "page" && key !== "pageSize") {
                    draft.params.page = 1;
                }
            }
        }
    },
    "xEventList/BeginLoading": (draft: Draft<EventListState>, action: { parameter: IEventListParams }) => {
        const key = getDataViewKey(action.parameter);
        draft.dataViews[key] = { value: undefined, isLoading: true };
    },
    "xEventList/CompleteLoading": (
        draft: Draft<EventListState>,
        action: { parameter: IEventListParams; events: IEventCollectionModel },
    ) => {
        const key = getDataViewKey(action.parameter);
        draft.dataViews[key] = {
            value: action.events,
            isLoading: false,
        };
    },
    "xEventList/Faild": (draft: Draft<EventListState>, action: { parameter: IEventListParams }) => {
        const key = getDataViewKey(action.parameter);
        draft.dataViews[key] = {
            isLoading: false,
        };
    },
});

(reducers as any).xEventList = xEventList;

export type xEventListState = {
    xEventList: EventListState;
} & ApplicationState;

export function getEventListDataView(state: xEventListState, parameters?: IEventListParams): EventListDataView {
    const key = getDataViewKey(parameters || state.xEventList.params);
    const dataView = state.xEventList?.dataViews?.[key];
    if (dataView) {
        return dataView;
    }
    return dataViewNotFound as EventListDataView;
}

type xEventListReducers = {
    xEventList: typeof xEventList;
};

export type xEventListActions = Parameters<xEventListReducers[keyof xEventListReducers]>[1] | AnyAction;
